import { Component } from '@angular/core';

import { translate } from '@jsverse/transloco';
import { titleCase } from 'humanize-plus';

import { CustomFieldsService } from 'app/modules/field/custom-fields.service';
import { AuthService } from 'app/providers/bridge-api/auth.service';
import { Comparison } from 'app/providers/bridge-api/dashboard.service';
import { FieldService } from 'app/providers/storage-api/field.service';
import { InteractiveSmartSummaryService } from 'app/providers/storage-api/interactive-smart-summary.service';
import { DownloadTasksService } from 'app/providers/tasks-api/download.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { TimezoneService } from 'app/services/timezone.service';
import { Utils } from 'app/utils/utils';
import { LexDatePipe } from '../../../../pipes/lex-date.pipe';
import {
  FieldStatisticsPayload,
  FieldStatisticsService,
} from '../../../../providers/field-statistics.service';
import { StorageAccountService } from '../../../../providers/storage-api/blender-account.service';
import { DocumentService } from '../../../../providers/storage-api/document.service';
import { ProjectService } from '../../../../providers/storage-api/project.service';
import { AlertService } from '../../../../services/alert.service';
import { DialogService } from '../../../../services/dialog.service';
import { LoggerService } from '../../../../services/logger.service';
import { SystemService } from '../../../../services/system.service';
import { WidgetLimiterService } from '../../../../services/widget-limiter.service';
import { FilterService, FilterSet } from '../../../collection/services/filter.service';
import { DashboardViewComponent } from '../../../dashboard/pages/dashboard-view/dashboard-view.component';
import { ChartCustomColorService } from '../../../dashboard/services/chart-custom-color.service';
import { ComparisonHandlerService } from '../../../dashboard/services/comparison-handler-service';
import { WidgetComponent } from '../widget/widget.component';
import { WidgetAnalyticItemDialogResult } from '../widget-analytic-item-dialog/widget-analytic-item-dialog.component';
import { WidgetChartComponent } from '../widget-chart/widget-chart.component';
import { ChartAiInsightPointSelectionService } from '../widget-expansion-panel/widget-ai-insights/chart-ai-insight-point-selection.service';
import { ChartWidgetAiInsightsValidatorService } from '../widget-expansion-panel/widget-ai-insights/services/validation/chart-widget-ai-insights-validator.service';
import { WidgetAiInsightsValidatorService } from '../widget-expansion-panel/widget-ai-insights/services/validation/widget-ai-insights-validator.service';
import { ChartCommentsService } from '../../services/chart-comments.service';
import { ChartDisplayService, HighchartsOptions } from '../../services/chart-display.service';
import { ChartReportService } from '../../services/chart-report.service';
import { ChartTableService } from '../../services/chart-table.service';
import { CpppCalculationService } from '../../services/cppp-calculation.service';
import { WidgetCommentsHandlerService } from '../../services/widget-comments-handler.service';

export interface RowProperty<T = string> {
  name: string;
  resolver: (value: T) => string | number;
}

@EnableClassTelemetry()
@Component({
  selector: 'app-widget-chart-volume',
  templateUrl: '../widget-chart/widget-chart.component.html',
  styleUrls: ['../widget-chart/widget-chart.component.scss'],
  providers: [
    ChartAiInsightPointSelectionService,
    { provide: WidgetAiInsightsValidatorService, useClass: ChartWidgetAiInsightsValidatorService },
  ],
  standalone: false,
})
export class WidgetChartVolumeComponent extends WidgetChartComponent {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  private missing = 0;
  private previousMissing = 0;
  private comparisonMissings = new Map<number, number>();

  constructor(
    private fieldStatisticsService: FieldStatisticsService,
    documentService: DocumentService,
    chartReportService: ChartReportService,
    chartDisplayService: ChartDisplayService,
    chartCommentsService: ChartCommentsService,
    chartPointSelectionService: ChartAiInsightPointSelectionService,
    chartCustomColorService: ChartCustomColorService,
    chartTableService: ChartTableService,
    systemService: SystemService,
    filterService: FilterService,
    widgetLimiterService: WidgetLimiterService,
    alertService: AlertService,
    dialogService: DialogService,
    projectService: ProjectService,
    dashboardViewComponent: DashboardViewComponent,
    widgetComponent: WidgetComponent,
    logger: LoggerService,
    accountService: StorageAccountService,
    lexDatePipe: LexDatePipe,
    cpppService: CpppCalculationService,
    authService: AuthService,
    downloadService: DownloadTasksService,
    comparisonService: ComparisonHandlerService,
    timezoneService: TimezoneService,
    protected interactiveSmartSummaryService: InteractiveSmartSummaryService,
    protected customFieldService: CustomFieldsService,
    protected commentsHandlerService: WidgetCommentsHandlerService
  ) {
    super(
      documentService,
      chartReportService,
      chartDisplayService,
      chartCommentsService,
      chartPointSelectionService,
      chartCustomColorService,
      chartTableService,
      systemService,
      filterService,
      widgetLimiterService,
      alertService,
      dialogService,
      projectService,
      dashboardViewComponent,
      widgetComponent,
      logger,
      accountService,
      lexDatePipe,
      cpppService,
      authService,
      downloadService,
      comparisonService,
      timezoneService,
      interactiveSmartSummaryService,
      customFieldService,
      commentsHandlerService
    );
  }

  public async refresh(
    resetUserDateInterval: boolean = false,
    initialLoad: boolean = false
  ): Promise<void> {
    if (this.widget.payload.showBlanks) {
      this.comparisonMissings.clear();

      const getStatsForCollection = async (filterQuery) => {
        return await this.fieldStatisticsService.getStatsForCollection(
          this.payload.projectId,
          this.widgetDataSource.collectionName,
          this.widgetDataSource.groupId,
          'xxx',
          this.analyticItem,
          undefined,
          filterQuery,
          this.timezoneService.timezone
        );
      };

      const assembleWidgetQueryOptions = (periodParam?, comparisonFilterSet?: FilterSet) => {
        return this.cpppService.assembleWidgetQueryOptions(
          this.widget,
          this.widgetDataSource,
          this.widgetGroup,
          periodParam,
          undefined,
          comparisonFilterSet
        );
      };

      if (this.comparison) {
        const promises: Promise<FieldStatisticsPayload>[] = [];

        this.comparison.filters.forEach((f) => {
          const widgetQueryOptions = assembleWidgetQueryOptions(null, f);

          promises.push(
            getStatsForCollection(
              this.filterService.getQueryForDocuments(widgetQueryOptions, false, true)
            )
          );
        });

        const payloads = await Promise.all(promises);

        payloads.forEach((p, i) =>
          this.comparisonMissings.set(i, p.custom[this.analyticItem]?.missing ?? 0)
        );
      } else {
        let widgetQueryOptions = assembleWidgetQueryOptions(null);
        if (this.widgetGroup?.isImpact) {
          const previousWidgetQueryOptions = assembleWidgetQueryOptions({
            first: this.widgetGroup?.parameters?.previousStart,
            last: this.widgetGroup?.parameters?.previousEnd,
          });
          const previousCalculations = await getStatsForCollection(
            this.filterService.getQueryForDocuments(previousWidgetQueryOptions, false, true)
          );
          this.previousMissing = previousCalculations.custom[this.analyticItem].missing ?? 0;

          widgetQueryOptions = assembleWidgetQueryOptions({
            first: this.widgetGroup.parameters.currentStart,
            last: this.widgetGroup.parameters.currentEnd,
          });
        }
        const calculations = await getStatsForCollection(
          this.filterService.getQueryForDocuments(widgetQueryOptions, false, true)
        );
        this.missing = calculations.custom[this.analyticItem]?.missing ?? 0;
      }
    }

    await super.refresh(resetUserDateInterval, initialLoad);
  }

  protected async calculateCurrentPeriod(reportOptions): Promise<any> {
    const response: any = {};

    if (!this.widgetGroup?.isImpact) {
      return response;
    }

    const currentWidgetQueryOptions = this.cpppService.assembleWidgetQueryOptions(
      this.widget,
      this.widgetDataSource,
      this.widgetGroup,
      {
        first: this.widgetGroup.parameters.currentStart,
        last: this.widgetGroup.parameters.currentEnd,
      }
    );

    const currentPeriodData = await this.executeReport(
      'json',
      this.widget.payload.drilledDownOn,
      this.filterService.getQueryForDocuments(currentWidgetQueryOptions, false, true)
    );
    response.currentPeriodData = currentPeriodData;

    this.report.options.currentPeriodDocumentCount = this.report.lastDocumentCount;
    this.filteredDocumentCount += this.report.lastDocumentCount;

    reportOptions.selectedItems = this.extractItemsFromResponse(currentPeriodData, reportOptions);

    return response;
  }

  protected async calculateComparisonBuckets(response: any, reportOptions: any) {
    reportOptions.selectedItems = this.extractItemsFromResponse(response, reportOptions);

    return super.calculateComparisonBuckets(response, reportOptions);
  }

  private extractItemsFromResponse(response, reportOptions) {
    if (reportOptions.selectedItems.length) {
      return reportOptions.selectedItems;
    }
    if (response.reportName === 'featureTopThemes') {
      return Object.keys(response.items);
    }
    if (response.reportName.includes('OverTime')) {
      let firstBucket: any = Object.values(response.items)[0];
      if (response.reportName.includes('taxonomy')) {
        firstBucket = firstBucket.count;
      }
      return Object.keys(firstBucket || []).filter((k) => k !== 'documentCount');
    }
    return Object.keys(response.items.count);
  }

  private handleBlankColumn(): void {
    const BLANK = '&lt;blank&gt;';
    let blankHasBeenAdded = false;

    const addBlankData = (missing, count, i) => {
      const blankValue = (m) =>
        this.widget.payload.aggregator === 'percentage' ? (m / count) * 100 : m;

      if (missing > 0) {
        this.highchartsOptions.series[i].data.push({
          x: this.highchartsOptions.series[i].data.length,
          y: blankValue(missing),
          documentCount: missing,
          pointId: BLANK,
          id: BLANK,
        });

        blankHasBeenAdded = true;
      }
    };

    if (this.comparison) {
      this.comparisonMissings.forEach((v, k) =>
        addBlankData(
          v,
          this.report.options.comparisonBucketsCounts?.[k] ?? this.filteredDocumentCount,
          k
        )
      );
    } else {
      addBlankData(this.missing, this.filteredDocumentCount, 0);
      if (this.widgetGroup?.isImpact) {
        addBlankData(this.previousMissing, this.filteredDocumentCount, 1);
      }
    }

    if (blankHasBeenAdded) {
      this.highchartsOptions.xAxis.categories.push(BLANK);

      if (this.payload.comments) {
        this.highchartsOptions.annotations = this.report.createChartAnnotations(
          this.highchartsOptions.series,
          this.widget,
          this.dashboard
        );

        this.highchartsOptions.annotations?.forEach((a) => {
          a.events = {
            click: this.annotationClickHandlerFn(),
          };
        });
      }
    }

    this.highchartsOptions = this.chartCustomColorService.setChartItemColors(
      this.widget,
      this.highchartsOptions
    );
  }

  public analyticTypeSet(result: WidgetAnalyticItemDialogResult): void {
    if (this.analyticItems.includes(result.analyticItem)) {
      delete this.widget.payload.showBlanks;
      return;
    }

    if (result.showBlanksChanged) {
      if (this.widget.payload.showBlanks) {
        delete this.widget.payload.showBlanks;
      } else {
        this.widget.payload.showBlanks = true;
      }
    }
  }

  protected formatHighchartsData(highchartsOptions: HighchartsOptions) {
    if (!this.widget.payload.aggregator.includes('time') && this.widget.payload.showBlanks) {
      this.handleBlankColumn();
    }

    if (!this.widgetGroup?.isImpact) {
      return highchartsOptions;
    }

    //If CPPP Volume chart
    highchartsOptions.series[0].name = translate('widgets.charting.current');
    highchartsOptions.series[0].showInLegend = true;

    //Swap Previous/Current positions on graph
    [highchartsOptions.series[0], highchartsOptions.series[1]] = [
      highchartsOptions.series[1],
      highchartsOptions.series[0],
    ];
    const prevData = highchartsOptions.series[0].data;
    const currData = highchartsOptions.series[1].data;

    //Add current items to previous data set if not present and set value to null
    for (const currItem of currData) {
      const idx = prevData.findIndex((prevItem) => prevItem.pointId === currItem.pointId);

      if (idx === -1) {
        prevData.push({ y: null, pointId: currItem.pointId });
      }
    }

    //Sort previous data to match current data chart position
    let sortedPrevData = prevData.sort((a, b) => {
      return (
        currData.findIndex((i) => i.pointId === a.pointId) -
        currData.findIndex((i) => i.pointId === b.pointId)
      );
    });

    //Add updated index to sorted previous data items
    sortedPrevData = sortedPrevData.map((item, index) => {
      if (item.hasOwnProperty('x')) {
        return { ...item, x: index };
      } else {
        return { x: index, ...item };
      }
    });

    highchartsOptions.series[0].data = [...sortedPrevData];

    return highchartsOptions;
  }

  public async exportAsCsv(): Promise<{ data: any }> {
    if (
      !this.widgetGroup?.isImpact &&
      !this.isShowingPercentage &&
      !this.widget.payload.showBlanks &&
      !(this.comparison?.filters.length > 1)
    ) {
      return super.exportAsCsv();
    }

    const percentageHeaderDecorator = (headers: string[]): string[] => {
      headers.splice(1, 0, translate('widgets.csv.percentage'));

      return headers;
    };

    const percentageRowPropDecorator = (props: RowProperty<number>[]): RowProperty<number>[] => {
      props.splice(1, 0, { name: 'percentage', resolver: (val) => Utils.round(val * 100, 1) });

      return props;
    };

    const impactHeaderDecorator = (headers: string[]): string[] => {
      for (let i = 1; i < headers.length; i++) {
        const h = headers[i];

        headers[i] = translate('widgets.csv.header-current', { header: h });
        headers.splice(i + 1, 0, translate('widgets.csv.header-previous', { header: h }));
        i++;
      }

      return headers;
    };

    const impactRowPropDecorator = (props: RowProperty[]): RowProperty[] => {
      for (let i = 0; i < props.length; i++) {
        const p = props[i];
        if (p.name === 'name') {
          continue;
        }

        props[i] = {
          name: `current${titleCase(p.name)}`,
          resolver: (val) => val,
        };

        props.splice(i + 1, 0, {
          name: `previous${titleCase(p.name)}`,
          resolver: (val) => val,
        });

        i++;
      }

      return props;
    };

    const comparisonPercentageHeaderDecorator = (): string[] => {
      const headers = [translate('widgets.csv.name')];
      const extractor = this.comparisonService.filterDisplayValueExtractorFn(this.comparison.type);
      for (const filterSet of this.comparison.filters) {
        const bucketLabel = extractor(filterSet);
        headers.push(translate('widgets.csv.header-percentage', { header: bucketLabel }));
        headers.push(translate('widgets.csv.header-count', { header: bucketLabel }));
      }
      return headers;
    };

    const comparisonPercentageRowDecorator = (
      props: RowProperty<number>[]
    ): RowProperty<number>[] => {
      props.splice(1, 1);

      const extractor = this.comparisonService.filterDisplayValueExtractorFn(this.comparison.type);
      for (const filterSet of this.comparison.filters) {
        const bucketLabel = extractor(filterSet);
        props.push({
          name: translate('widgets.csv.header-percentage', { header: bucketLabel }),
          resolver: (val) => Utils.round(val * 100, 1),
        });
        props.push({
          name: translate('widgets.csv.header-count', { header: bucketLabel }),
          resolver: (val) => val,
        });
      }
      return props;
    };

    const comparisonHeaderDecorator = (props: string[]): string[] => {
      props.pop();
      const extractor = this.comparisonService.filterDisplayValueExtractorFn(this.comparison.type);
      for (const filterSet of this.comparison.filters) {
        props.push(extractor(filterSet));
      }
      return props;
    };

    const comparisonRowPropDecorator = (
      props: RowProperty<{ count: number; percentage?: number }[]>[]
    ): RowProperty<{ count: number; percentage?: number }[]>[] => {
      const filterCount = this.comparison.filters.length;
      props[props.length - 1] = {
        name: 'buckets',
        resolver: (val: { count: number; percentage?: number }[]) => {
          const values = val.map((v) => v.count ?? 0);

          const zeroArray: number[] = new Array(filterCount).fill(0);
          return Array.from(zeroArray, (x, i) => values[i] ?? x).join();
        },
      };
      return props;
    };

    let headers = [translate('widgets.csv.name'), translate('widgets.csv.count')];
    let rowProps: RowProperty<any>[] = [
      {
        name: 'name',
        resolver: (val) => `"${Utils.escapeCsvString(val, false)}"`,
      },
      {
        name: 'count',
        resolver: (val) => val,
      },
    ];

    if (this.isShowingPercentage) {
      if (!this.comparison) {
        headers = percentageHeaderDecorator(headers);
        rowProps = percentageRowPropDecorator(rowProps);
      } else {
        headers = comparisonPercentageHeaderDecorator();
        rowProps = comparisonPercentageRowDecorator(rowProps);
      }
    } else if (this.widgetGroup?.isImpact) {
      headers = impactHeaderDecorator(headers);
      rowProps = impactRowPropDecorator(rowProps);
    } else if (this.comparison) {
      headers = comparisonHeaderDecorator(headers);
      rowProps = comparisonRowPropDecorator(rowProps);
    }

    let data = headers.map((h) => Utils.escapeCsvString(h)).join() + '\n';

    if (!this.comparison || !this.isShowingPercentage) {
      for (const row of this.dataSource) {
        data += rowProps.map((prop) => prop.resolver(row[prop.name])).join();

        data += '\n';
      }
    } else {
      for (const row of this.dataSource) {
        data += `${rowProps[0].resolver(row['name'])},`;

        const bucketValues = [];
        for (let index = 0; index < this.comparison.filters.length; index++) {
          const bucket = row.buckets[index];

          if (!bucket) {
            bucketValues.push(0, 0);
            continue;
          }

          bucketValues.push(
            rowProps[1].resolver(bucket.percentage),
            rowProps[2].resolver(bucket.count)
          );
        }

        data += `${bucketValues.join()}\n`;
      }
    }

    return { data };
  }

  public async showDocumentsForItem(initialQuery?: string): Promise<void> {
    if (this.widgetGroup?.isImpact) {
      this.activePeriodParam.first = this.widgetContextMenu.series.index
        ? 'currentStart'
        : 'previousStart';

      this.activePeriodParam.last = this.widgetContextMenu.series.index
        ? 'currentEnd'
        : 'previousEnd';
    }

    await super.showDocumentsForItem(initialQuery);

    this.activePeriodParam.first = 'currentStart';
    this.activePeriodParam.last = 'currentEnd';
  }

  protected createDataTable(response: any): void {
    const dataTableCreationOption = {
      analyticItemFieldType: FieldService.getFieldType(this.payload.analyticItem),
    };

    super.createDataTable(
      response,
      this.missing,
      this.previousMissing,
      this.comparisonMissings,
      dataTableCreationOption
    );

    if (this.isShowingPercentage && this.widgetGroup?.isImpact) {
      this.displayedColumns.push('count_current');
      this.displayedColumns.push('count_previous');
    }
  }

  get canSort(): boolean {
    return !this.widgetGroup?.isImpact;
  }

  get canChangeItemColor() {
    return !this.widgetGroup?.isImpact;
  }

  get isShowingPercentage() {
    return this.widget.payload.aggregator === 'percentage';
  }

  canApplyComparison(comparison?: Comparison): boolean {
    if (this.widget.payload.graphType === 'bar' || this.widget.payload.graphType === 'column') {
      return true;
    }

    return super.canApplyComparison(comparison);
  }

  handleChartOptionsChanging(): void {
    // Can be removed once we support Reveal for Volume over time
    this.widgetComponent.initWidgetActions();
    this.widgetComponent.pillWrapper.checkCompactSize();
  }
}
